import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { ConfigureSubstitutesModal } from '../components/ConfigureSubstitutesModal';
import { Clock, Package, Calendar, Truck, Lock, Settings, Info, ExternalLink, ChevronRight } from 'lucide-react';
interface SettingsOrderProcessingPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function SettingsOrderProcessingPage({
  currentPage = 'Settings',
  onNavigate
}: SettingsOrderProcessingPageProps) {
  const navigate = useNavigate();
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Modal state
  const [showSubstitutesModal, setShowSubstitutesModal] = useState(false);
  // Section 1: Processing Schedule
  const [processingTime, setProcessingTime] = useState('08:00');
  const [bufferDays, setBufferDays] = useState(2);
  // Section 2: Inventory Management
  const [checkInventory, setCheckInventory] = useState(true);
  const [autoSubstitute, setAutoSubstitute] = useState(false);
  const [reserveInventory, setReserveInventory] = useState(false);
  // Section 3: Subscription Behavior
  const [nextOrderCalculation, setNextOrderCalculation] = useState('last-order');
  const [recalculateFrequency, setRecalculateFrequency] = useState(true);
  const [autoPauseEnabled, setAutoPauseEnabled] = useState(false);
  const [autoPauseCount, setAutoPauseCount] = useState<number | ''>('');
  // Section 4: Shipping Settings
  const [chargeShipping, setChargeShipping] = useState(false);
  const [localPickup, setLocalPickup] = useState(false);
  const [localDelivery, setLocalDelivery] = useState(false);
  // Section 5: Order Merging state
  const [orderMergingEnabled, setOrderMergingEnabled] = useState(false);
  const [mergeOrdersWindow, setMergeOrdersWindow] = useState('3');
  const [mergeOneTimePurchases, setMergeOneTimePurchases] = useState(false);
  // Section 6: Advanced Options
  const [copyNotes, setCopyNotes] = useState(false);
  const [preserveAttributes, setPreserveAttributes] = useState(true);
  const [transferAttributes, setTransferAttributes] = useState(false);
  // Navigation handler
  const handleNavigate = onNavigate || ((page: string) => {
    if (page === 'Dashboard') navigate('/');else if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else if (page === 'Contacts' || page === 'Contact') navigate('/contacts');else if (page === 'Club Builder') navigate('/club-builder');else if (page === 'Products') navigate('/products');else if (page === 'Tools') navigate('/management/tools');else if (page === 'Tag Management' || page === 'Tags') navigate('/settings/tags');else if (page === 'Settings' || page === 'General') navigate('/settings/general');else if (page === 'Order Processing') navigate('/settings/order-processing');else if (page === 'Account and Billing') navigate('/settings/account-billing');else navigate('/');
  });
  // Save handler (simulated)
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  // Toggle handler
  const handleToggle = (setter: React.Dispatch<React.SetStateAction<boolean>>, value: boolean, settingName: string) => {
    setter(value);
    saveSetting();
  };
  // Radio handler
  const handleRadio = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    setter(value);
    saveSetting();
  };
  // Input handler (with debounce simulation)
  const handleInputBlur = () => {
    saveSetting();
  };
  // Handle substitute save
  const handleSaveSubstitutes = (mappings: any) => {
    saveSetting('Substitute settings saved');
  };
  // Generate time options
  const timeOptions = Array.from({
    length: 24
  }, (_, i) => {
    const hour = i;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const value = `${hour.toString().padStart(2, '0')}:00`;
    const label = `${displayHour}:00 ${ampm}`;
    return {
      value,
      label
    };
  });
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-9114">
      {/* NavBar */}
      <div className="mb-6" data-id="element-9115">
        <NavBar tabs={[]} title="Order Processing" data-id="element-9116" />
      </div>

      {/* Page Header */}
      <div className="mb-8" data-id="element-9117">
        <p className="text-[14px] text-[#64748B]" data-id="element-9118">
          Configure how subscription orders are created and processed
        </p>
      </div>

      {/* Main Content */}
      <div className="space-y-6" data-id="element-9119">
        {/* Section 1: Processing Schedule */}
        <Card padding="none" data-id="element-9120">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-9121">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-9122">
              <Clock className="w-5 h-5 text-[#0EA5E9]" data-id="element-9123" />
            </div>
            <div data-id="element-9124">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9125">
                Processing Schedule
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-9126">
                Control when subscription orders are created
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-9127">
            {/* Daily Processing Time */}
            <div data-id="element-9128">
              <label className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-9129">
                Daily Processing Time
              </label>
              <select value={processingTime} onChange={e => {
              setProcessingTime(e.target.value);
              saveSetting();
            }} className="w-full h-[40px] px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-9130">
                {timeOptions.map(option => <option key={option.value} value={option.value} data-id="element-9131">
                    {option.label}
                  </option>)}
              </select>
              <p className="mt-2 text-[12px] text-[#64748B]" data-id="element-9132">
                Orders scheduled for each day will be created at this time in
                your store's timezone (Australia/Melbourne)
              </p>
            </div>

            {/* Processing Buffer Days */}
            <div data-id="element-9133">
              <label className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-9134">
                Processing Buffer Days
              </label>
              <div className="flex items-center gap-3" data-id="element-9135">
                <div className="relative w-32" data-id="element-9136">
                  <input type="number" min="0" max="14" value={bufferDays} onChange={e => setBufferDays(parseInt(e.target.value) || 0)} onBlur={handleInputBlur} className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow" data-id="element-9137" />
                </div>
                <span className="text-[14px] text-[#64748B]" data-id="element-9138">days</span>
              </div>
              <p className="mt-2 text-[12px] text-[#64748B]" data-id="element-9139">
                Number of days before the scheduled date to create the order.
                Gives time for customer notifications and changes.
              </p>
            </div>
          </div>
        </Card>

        {/* Section 2: Inventory Management */}
        <Card padding="none" data-id="element-9140">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-9141">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-9142">
              <Package className="w-5 h-5 text-[#0EA5E9]" data-id="element-9143" />
            </div>
            <div data-id="element-9144">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9145">
                Inventory Management
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-9146">
                Control how inventory affects subscription orders
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-9147">
            {/* Toggle 1: Check Inventory */}
            <div className="flex items-start justify-between" data-id="element-9148">
              <div className="flex-1 pr-6" data-id="element-9149">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9150">
                  Check Inventory Before Processing
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9151">
                  Verify product availability before creating subscription
                  orders. Orders with out-of-stock items will be held.
                </p>
              </div>
              <button onClick={() => handleToggle(setCheckInventory, !checkInventory, 'Check Inventory')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${checkInventory ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={checkInventory} data-id="element-9152">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checkInventory ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9153" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-9154" />

            {/* Toggle 2: Auto-substitute */}
            <div className="flex items-start justify-between" data-id="element-9155">
              <div className="flex-1 pr-6" data-id="element-9156">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9157">
                  Auto-substitute Out of Stock Products
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9158">
                  Automatically replace out-of-stock products with designated
                  alternatives
                </p>
                {autoSubstitute && <button onClick={() => setShowSubstitutesModal(true)} className="mt-2 inline-flex items-center gap-1 text-[14px] text-[#0EA5E9] hover:underline font-medium" data-id="element-9159">
                    Configure Substitutes
                    <ExternalLink className="w-3 h-3" data-id="element-9160" />
                  </button>}
              </div>
              <button onClick={() => handleToggle(setAutoSubstitute, !autoSubstitute, 'Auto-substitute')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${autoSubstitute ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={autoSubstitute} data-id="element-9161">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoSubstitute ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9162" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-9163" />

            {/* Toggle 3: Reserve Inventory */}
            <div className="flex items-start justify-between" data-id="element-9164">
              <div className="flex-1 pr-6" data-id="element-9165">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9166">
                  Reserve Inventory for Upcoming Orders
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9167">
                  Reserve stock for subscription orders 7 days before processing
                </p>
              </div>
              <button onClick={() => handleToggle(setReserveInventory, !reserveInventory, 'Reserve Inventory')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${reserveInventory ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={reserveInventory} data-id="element-9168">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${reserveInventory ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9169" />
              </button>
            </div>
          </div>
        </Card>

        {/* Section 3: Subscription Behavior */}
        <Card padding="none" data-id="element-9170">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-9171">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-9172">
              <Calendar className="w-5 h-5 text-[#0EA5E9]" data-id="element-9173" />
            </div>
            <div data-id="element-9174">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9175">
                Subscription Behavior
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-9176">
                Define how subscriptions calculate dates and handle changes
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-9177">
            {/* Next Order Date Calculation */}
            <div data-id="element-9178">
              <label className="block text-[14px] font-medium text-[#475569] mb-3" data-id="element-9179">
                When frequency changes, calculate next order from:
              </label>
              <div className="space-y-4" data-id="element-9180">
                {[{
                id: 'today',
                label: "Today's date",
                desc: 'Next order scheduled from the date of the change'
              }, {
                id: 'last-order',
                label: 'Last order date',
                desc: 'Next order scheduled from when the last order was placed'
              }, {
                id: 'billing-date',
                label: 'Current billing date',
                desc: 'Keep the existing billing date, apply new frequency going forward'
              }].map(option => <div key={option.id} className="flex items-start cursor-pointer" onClick={() => handleRadio(setNextOrderCalculation, option.id)} data-id="element-9181">
                    <div className={`mt-0.5 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${nextOrderCalculation === option.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-9182">
                      {nextOrderCalculation === option.id && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-9183" />}
                    </div>
                    <div className="ml-3" data-id="element-9184">
                      <span className="block text-[14px] font-medium text-[#0F172A]" data-id="element-9185">
                        {option.label}
                      </span>
                      <span className="block text-[13px] text-[#64748B] mt-0.5" data-id="element-9186">
                        {option.desc}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-9187" />

            {/* Recalculate on Frequency Change */}
            <div className="flex items-start justify-between" data-id="element-9188">
              <div className="flex-1 pr-6" data-id="element-9189">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9190">
                  Recalculate on Frequency Change
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9191">
                  Automatically recalculate the next charge date when a customer
                  changes their delivery frequency
                </p>
              </div>
              <button onClick={() => handleToggle(setRecalculateFrequency, !recalculateFrequency, 'Recalculate Frequency')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${recalculateFrequency ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={recalculateFrequency} data-id="element-9192">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${recalculateFrequency ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9193" />
              </button>
            </div>
          </div>
        </Card>

        {/* Section 4: Shipping Settings */}
        <Card padding="none" data-id="element-9194">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-9195">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-9196">
              <Truck className="w-5 h-5 text-[#0EA5E9]" data-id="element-9197" />
            </div>
            <div data-id="element-9198">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9199">
                Shipping Settings
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-9200">
                Configure shipping for subscription orders
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-9201">
            {/* Toggle 1: Charge Shipping */}
            <div className="flex items-start justify-between" data-id="element-9202">
              <div className="flex-1 pr-6" data-id="element-9203">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9204">
                  Charge Shipping on Recurring Orders
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9205">
                  Apply shipping charges to recurring subscription orders. Most
                  wine clubs offer free shipping for members.
                </p>
              </div>
              <button onClick={() => handleToggle(setChargeShipping, !chargeShipping, 'Charge Shipping')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${chargeShipping ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={chargeShipping} data-id="element-9206">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${chargeShipping ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9207" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-9208" />

            {/* Toggle 2: Local Pickup */}
            <div className="flex items-start justify-between" data-id="element-9209">
              <div className="flex-1 pr-6" data-id="element-9210">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9211">
                  Enable Local Pickup
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9212">
                  Allow customers to select local pickup instead of delivery for
                  their subscription orders
                </p>
                {localPickup && <button className="mt-2 inline-flex items-center gap-1 text-[14px] text-[#0EA5E9] hover:underline font-medium" data-id="element-9213">
                    Configure Pickup Locations
                    <ExternalLink className="w-3 h-3" data-id="element-9214" />
                  </button>}
              </div>
              <button onClick={() => handleToggle(setLocalPickup, !localPickup, 'Local Pickup')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${localPickup ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={localPickup} data-id="element-9215">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${localPickup ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9216" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-9217" />

            {/* Toggle 3: Local Delivery */}
            <div className="flex items-start justify-between" data-id="element-9218">
              <div className="flex-1 pr-6" data-id="element-9219">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9220">
                  Enable Local Delivery
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9221">
                  Offer local delivery option for customers within your delivery
                  zone
                </p>
                {localDelivery && <button className="mt-2 inline-flex items-center gap-1 text-[14px] text-[#0EA5E9] hover:underline font-medium" data-id="element-9222">
                    Configure Delivery Zones
                    <ExternalLink className="w-3 h-3" data-id="element-9223" />
                  </button>}
              </div>
              <button onClick={() => handleToggle(setLocalDelivery, !localDelivery, 'Local Delivery')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${localDelivery ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={localDelivery} data-id="element-9224">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${localDelivery ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9225" />
              </button>
            </div>
          </div>
        </Card>

        {/* Section 5: Order Merging */}
        <Card padding="none" data-id="element-9226">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-9227">
            <div className="flex items-center gap-3" data-id="element-9228">
              <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-9229">
                <Package className="w-5 h-5 text-[#0EA5E9]" data-id="element-9230" />
              </div>
              <div data-id="element-9231">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9232">
                  Order Merging
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-9233">
                  Combine subscription orders with one-time purchases to save on
                  shipping
                </p>
              </div>
            </div>
            <button onClick={() => handleToggle(setOrderMergingEnabled, !orderMergingEnabled, 'Order Merging')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${orderMergingEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={orderMergingEnabled} data-id="element-9234">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${orderMergingEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9235" />
            </button>
          </div>

          {orderMergingEnabled && <div className="p-6 space-y-6" data-id="element-9236">
              {/* Merge Orders Within */}
              <div data-id="element-9237">
                <label className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-9238">
                  Merge orders within
                </label>
                <select value={mergeOrdersWindow} onChange={e => {
              setMergeOrdersWindow(e.target.value);
              saveSetting();
            }} className="w-full h-[40px] px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-9239">
                  <option value="1" data-id="element-9240">1 day</option>
                  <option value="3" data-id="element-9241">3 days</option>
                  <option value="5" data-id="element-9242">5 days</option>
                  <option value="7" data-id="element-9243">7 days</option>
                  <option value="14" data-id="element-9244">14 days</option>
                </select>
                <p className="mt-2 text-[12px] text-[#64748B]" data-id="element-9245">
                  One-time purchases placed within this window of a subscription
                  order will be combined into a single shipment
                </p>
              </div>
            </div>}
        </Card>

        {/* Section 6: Advanced Options */}
        <Card padding="none" data-id="element-9246">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-9247">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-9248">
              <Settings className="w-5 h-5 text-[#0EA5E9]" data-id="element-9249" />
            </div>
            <div data-id="element-9250">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9251">
                Advanced Options
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-9252">
                Additional order processing settings
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-9253">
            {/* Toggle 1: Copy Order Notes */}
            <div className="flex items-start justify-between" data-id="element-9254">
              <div className="flex-1 pr-6" data-id="element-9255">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9256">
                  Copy Order Notes to Subscription
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9257">
                  Copy notes from the original order to the subscription record
                </p>
              </div>
              <button onClick={() => handleToggle(setCopyNotes, !copyNotes, 'Copy Order Notes')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${copyNotes ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={copyNotes} data-id="element-9258">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${copyNotes ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9259" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-9260" />

            {/* Toggle 2: Preserve Attributes */}
            <div className="flex items-start justify-between" data-id="element-9261">
              <div className="flex-1 pr-6" data-id="element-9262">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9263">
                  Preserve Line Item Attributes
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9264">
                  Keep custom product attributes (like gift messages) on
                  recurring orders
                </p>
              </div>
              <button onClick={() => handleToggle(setPreserveAttributes, !preserveAttributes, 'Preserve Attributes')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${preserveAttributes ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={preserveAttributes} data-id="element-9265">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preserveAttributes ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9266" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-9267" />

            {/* Toggle 3: Transfer Attributes */}
            <div className="flex items-start justify-between" data-id="element-9268">
              <div className="flex-1 pr-6" data-id="element-9269">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9270">
                  Transfer Order Attributes
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9271">
                  Copy order-level attributes to each recurring order
                </p>
              </div>
              <button onClick={() => handleToggle(setTransferAttributes, !transferAttributes, 'Transfer Attributes')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${transferAttributes ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={transferAttributes} data-id="element-9272">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${transferAttributes ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9273" />
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Configure Substitutes Modal */}
      <ConfigureSubstitutesModal isOpen={showSubstitutesModal} onClose={() => setShowSubstitutesModal(false)} onSave={handleSaveSubstitutes} data-id="element-9274" />

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-9275">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-9276">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-9277" />
            <div className="flex-1" data-id="element-9278">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-9279">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-9280">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}